import React, { useState, useEffect, useRef } from 'react';
const styles = require('./styles.pcss');
import * as _ from 'lodash-es';
import Dialog from '@mui/material/Dialog';
import cx from 'classnames';
import { useForm, SubmitHandler, Controller, get } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  MenuItem,
  Select,
  TextField,
  OutlinedInput,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AIRLINE_NAME_MAP } from '../../helpers/airlinemap';
import AutoSuggest from 'components/Inputs/AutoSuggest/AutoSuggest';
import SvgAirplane from '../../static/icons/svgAirplane';

/** Rounds to 2 decimals and returns a finite number (0 for NaN/Infinity). */
const round2 = (n: number): number =>
  isFinite(n) ? parseFloat(n.toFixed(2)) : 0;

const HOURS_24 = Array.from({ length: 24 }, (_v, i) => String(i).padStart(2, '0'));
const MINUTES_60 = Array.from({ length: 60 }, (_v, i) => String(i).padStart(2, '0'));

/**
 * Hour/Minute dropdown pair for a 24hr "HH:mm" value. Replaces the native
 * <input type="time"> whose browser-rendered popup (hour/minute/AM-PM columns)
 * can't be restyled — this gives full control over row spacing and size.
 */
function TimeSelect(props: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const [h, m] = (props.value || '00:00').split(':');
  const hour = HOURS_24.includes(h) ? h : '00';
  const minute = MINUTES_60.includes(m) ? m : '00';
  return (
    <div className={styles.inlineFieldWrapper}>
      <span className={styles.inlineFieldLabel}>{props.label}</span>
      <div className={styles.timeSelectGroup}>
        <Select
          size="small"
          variant="outlined"
          value={hour}
          onChange={(e) => props.onChange(`${e.target.value}:${minute}`)}
          MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
        >
          {HOURS_24.map((hh) => (
            <MenuItem key={hh} value={hh}>{hh}</MenuItem>
          ))}
        </Select>
        <span className={styles.timeColon}>:</span>
        <Select
          size="small"
          variant="outlined"
          value={minute}
          onChange={(e) => props.onChange(`${hour}:${e.target.value}`)}
          MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
        >
          {MINUTES_60.map((mm) => (
            <MenuItem key={mm} value={mm}>{mm}</MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );
}

enum StatusEnum {
  completed = 'completed',
  canceled = 'canceled',
  pending = 'pending',
  errored = 'errored',
}

type Inputs = {
  email: string;
  status: StatusEnum;
  segments?: any;
  confirmationNumber?: string;
  // --- CAR BOOKING FIELDS ---
  basePrice?: number;
  totalPrice?: number;
  requiredCharges?: number;
  providerReturnDate?: string;
  // --- HOTEL BOOKING FIELDS ---
  taxesAndFees?: number;
  customerPrice?: number;
  resortFee?: number;
  departureDate?: string;
};

type Props = {
  open: boolean;
  close: any;
  type: string;
  booking: any;
  id: any;
  confirmationNumber: string;
  status: string;
  identifier: any;
  additionalEmails: string;
  userEmail: string;
  updateBooking: Function;
  notes?: string;
  // --- CAR BOOKING FIELDS ---
  basePrice?: number;
  totalPrice?: number;
  requiredCharges?: number;
  providerReturnDate?: string;
  // --- HOTEL BOOKING FIELDS ---
  taxesAndFees?: number;
  customerPrice?: number;
  resortFee?: number;
  departureDate?: string;
};

export default function ModifyBookingsDetailsPopup(props: Props) {
  // Resolve flat pricing fields with GDS fallback for bookings that pre-date
  // the flat-field migration (e.g. bookings fetched via PNR lookup whose taxes
  // are only stored in reservations[0].priceInfo.Taxes as "USD100.00").
  // Must be computed before useForm so they can be passed as defaultValues.
  const parseGdsCurrency = (raw: any): string => {
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'number') return String(raw);
    return String(raw).replace(/^[A-Z]{3}/, '');
  };
  const gdsBasePrice = _.get(props.booking, 'reservations[0].priceInfo.BasePrice', '');
  const gdsTaxPrice  = _.get(props.booking, 'reservations[0].priceInfo.Taxes', '');
  const defaultBasePrice  = props.booking?.basePrice  ?? parseGdsCurrency(gdsBasePrice);
  const defaultTaxPrice   = props.booking?.taxPrice   ?? parseGdsCurrency(gdsTaxPrice);
  const parsedBase  = parseFloat(String(defaultBasePrice));
  const parsedTax   = parseFloat(String(defaultTaxPrice));
  const defaultTotalPrice = props.booking?.totalPrice != null
    ? props.booking.totalPrice
    : (!isNaN(parsedBase) && !isNaN(parsedTax))
      ? (parsedBase + parsedTax).toFixed(2)
      : '';

  // Car defaults: pickup/return date+time come from the provider-local
  // timestamps (fall back to the UTC field), locations and class from the
  // nested car object, and pricePerDay from car.priceDetails.
  const carPickupRaw = String(
    _.get(props.booking, 'providerPickupDate') ||
      _.get(props.booking, 'pickupDateTime') ||
      '',
  );
  const carReturnRaw = String(
    _.get(props.booking, 'providerReturnDate') ||
      _.get(props.booking, 'returnDateTime') ||
      '',
  );
  const carPricePerDay = _.get(props.booking, 'car.priceDetails.pricePerDay');
  // Days comes only from the stored value — a return-date minus pickup-date
  // diff was found to disagree with the actual rental-day count (e.g. a
  // 9/30 noon → 10/2 4pm booking is 3 rental days, not 2 calendar days), so
  // it's never derived from the dates here.
  const carDaysStored = _.get(props.booking, 'car.priceDetails.days');
  const carDefaults =
    props.booking?.type === 'car'
      ? {
          pickupDate: carPickupRaw.substring(0, 10),
          pickupTime: carPickupRaw.substring(11, 16),
          returnDate: carReturnRaw.substring(0, 10),
          returnTime: carReturnRaw.substring(11, 16),
          days: carDaysStored != null ? Math.max(1, Math.round(Number(carDaysStored))) : 1,
          pricePerDay:
            carPricePerDay != null
              ? round2(parseFloat(String(carPricePerDay)))
              : undefined,
          carClass: _.get(props.booking, 'car.class', ''),
          pickupStreet: _.get(props.booking, 'car.location.pickup.street', ''),
          pickupCity: _.get(props.booking, 'car.location.pickup.city', ''),
          pickupState: _.get(props.booking, 'car.location.pickup.state', ''),
          pickupCountryCode: _.get(props.booking, 'car.location.pickup.countryCode', ''),
          pickupPostalCode: _.get(props.booking, 'car.location.pickup.postalCode', ''),
          returnStreet: _.get(props.booking, 'car.location.return.street', ''),
          returnCity: _.get(props.booking, 'car.location.return.city', ''),
          returnState: _.get(props.booking, 'car.location.return.state', ''),
          returnCountryCode: _.get(props.booking, 'car.location.return.countryCode', ''),
          returnPostalCode: _.get(props.booking, 'car.location.return.postalCode', ''),
          pickupCityCode: _.get(props.booking, 'pickupLocation', ''),
          returnCityCode: _.get(props.booking, 'dropoffLocation', ''),
        }
      : {};

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    setFocus,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      email: props.userEmail,
      ...(props.booking?.type === 'car' ? {
        basePrice: props.basePrice != null ? parseFloat(parseFloat(String(props.basePrice)).toFixed(2)) : undefined,
        requiredCharges: props.requiredCharges != null ? parseFloat(parseFloat(String(props.requiredCharges)).toFixed(2)) : undefined,
        totalPrice: props.totalPrice != null ? parseFloat(parseFloat(String(props.totalPrice)).toFixed(2)) : undefined,
        ...carDefaults,
      } : {}),
      ...(props.booking?.type === 'hotel' ? {
        basePrice: props.basePrice != null ? parseFloat(parseFloat(String(props.basePrice)).toFixed(2)) : undefined,
        taxesAndFees: props.taxesAndFees != null ? parseFloat(parseFloat(String(props.taxesAndFees)).toFixed(2)) : undefined,
        customerPrice: props.customerPrice != null ? parseFloat(parseFloat(String(props.customerPrice)).toFixed(2)) : undefined,
        resortFee: props.resortFee != null ? parseFloat(parseFloat(String(props.resortFee)).toFixed(2)) : undefined,
        departureDate: props.departureDate?.substring(0, 10) ?? undefined,
      } : {}),
    },
  });
  watch();

  const watchedBase = parseFloat(watch('basePrice')) || 0;
  const watchedTax  = parseFloat(watch('requiredCharges')) || 0;
  const watchedTaxesAndFees = parseFloat(watch('taxesAndFees')) || 0;
  const isFirstRender = useRef(true);

  // --- CAR: live cost recalculation ---
  const watchedPerDay = parseFloat(watch('pricePerDay')) || 0;
  const watchedTotal = parseFloat(watch('totalPrice')) || 0;
  // Days is fully agent-editable and independent of the pickup/return date
  // fields — no date-diff formula backs it (see carDefaults comment above).
  const watchedDays = parseInt(watch('days'), 10) || 1;
  // Tracks which field the agent last edited so recalculation flows in the
  // correct direction (see field-interaction rules) without looping.
  const carLastEdited = useRef<
    'days' | 'pricePerDay' | 'basePrice' | 'requiredCharges' | 'total' | 'none'
  >('none');
  const isFirstCarCalc = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (props.booking?.type === 'hotel') {
      // Total Paid = Room + Taxes & Fees
      setValue('customerPrice', parseFloat((watchedBase + watchedTaxesAndFees).toFixed(2)));
    }
  }, [watchedBase, watchedTax, watchedTaxesAndFees]);

  useEffect(() => {
    if (props.booking?.type !== 'car') return;
    if (isFirstCarCalc.current) {
      isFirstCarCalc.current = false;
      return;
    }
    switch (carLastEdited.current) {
      case 'total':
        // Reflect the real value (even negative) instead of clamping to 0 —
        // the invariant check below warns the agent and blocks submit.
        setValue('requiredCharges', round2(watchedTotal - watchedBase));
        break;
      case 'basePrice':
        if (watchedDays > 0) setValue('pricePerDay', round2(watchedBase / watchedDays));
        setValue('totalPrice', round2(watchedBase + watchedTax));
        break;
      case 'requiredCharges':
        setValue('totalPrice', round2(watchedBase + watchedTax));
        break;
      case 'days':
      case 'pricePerDay': {
        const newBase = round2(watchedDays * watchedPerDay);
        setValue('basePrice', newBase);
        setValue('totalPrice', round2(newBase + watchedTax));
        break;
      }
      default:
        break;
    }

    // Estimated Total can never be less than Base Price (Required Charges
    // would go negative). Warn inline instead of silently clamping.
    if (round2(watchedTotal - watchedBase) < 0) {
      setError('totalPrice', {
        type: 'manual',
        message: `Estimated Total cannot be less than Base Price (${watchedBase.toFixed(2)})`,
      });
    } else if (get(errors, 'totalPrice')?.type === 'manual') {
      clearErrors('totalPrice');
    }
  }, [watchedPerDay, watchedBase, watchedTax, watchedTotal, watchedDays]);

  const setCarSource = (source: typeof carLastEdited.current) => () => {
    carLastEdited.current = source;
  };

  let airlines: any = [];
  _.forOwn(AIRLINE_NAME_MAP, function (value, key) {
    airlines.push({ code: key, name: value });
  });

  const segments = props.booking?.segments?.reduce(
    (allSegments: any[], segmentGroup: any) => {
      allSegments.push(...segmentGroup.segment);
      return allSegments;
    },
    [],
  );

  const onClose = () => {
    reset();
    props.close();
  };

  const onSubmit: SubmitHandler<Inputs> = (data: any) => {
    // ── Existing PUT fields (status / confirmation / segments) ─────────────────
    data.segments = data.segments?.map((segment: any) => {
      // Existing booking stores airline as a code ("AA"); new selections use
      // the full name ("American Airlines"). Resolve whichever is present.
      const airlineEntry =
        airlines.find((a: any) => a.name === segment.airline) ??
        airlines.find((a: any) => a.code === segment.airline);
      const airlineCode = airlineEntry?.code ?? segment.airline ?? '';

      const {
        arrivalTime,
        arrivalDate,
        departureDate,
        departureTime,
        arrival: oldArrival,
        departure: oldDeparture,
      } = segment;

      const arrival = `${arrivalDate}T${arrivalTime}${oldArrival?.substring(16)}`;
      const departure = `${departureDate}T${departureTime}${oldDeparture?.substring(16)}`;

      return {
        ...segment,
        arrival,
        departure,
        airline: airlineCode,
      };
    });
    // Build the single PUT payload. Pricing/date fields are now handled by the
    // extended PUT /bookings/details endpoint — no separate PATCH call needed.
    const basePriceVal = parseFloat(data.basePrice);
    const taxPriceVal = parseFloat(data.taxPrice);
    const totalPriceVal = parseFloat(data.totalPrice);

    // Car: block submit when Estimated Total < Base Price (Required Charges
    // would go negative) and shift focus to the offending field.
    if (props.type === 'car' && isFinite(basePriceVal) && isFinite(totalPriceVal)) {
      if (round2(totalPriceVal - basePriceVal) < 0) {
        setError('totalPrice', {
          type: 'manual',
          message: `Estimated Total cannot be less than Base Price (${basePriceVal.toFixed(2)})`,
        });
        setFocus('totalPrice');
        return;
      }
    }

    const updatePayload: Record<string, any> = {
      bookingId: props.id,
      ...(data.email && { email: data.email }),
      status: data.status,
      confirmationNumber: data.confirmationNumber,
      notes: data.notes,
      segments: data.segments,
      travelerIds: data.travelerIds,
    };

    // Flight: only send pricing when changed — avoids 422 from totalPrice
    // consistency check on bookings where total != base + tax.
    if (props.type === 'flight') {
      const origBase = parseFloat(String(defaultBasePrice));
      const origTax = parseFloat(String(defaultTaxPrice));
      const origTotal = parseFloat(String(defaultTotalPrice));
      if (isFinite(basePriceVal) && basePriceVal !== origBase) updatePayload.basePrice = basePriceVal;
      if (isFinite(taxPriceVal) && taxPriceVal !== origTax) updatePayload.taxPrice = taxPriceVal;
      if (isFinite(totalPriceVal) && totalPriceVal !== origTotal) updatePayload.totalPrice = totalPriceVal;
      if (data.reservationStartDate) updatePayload.reservationStartDate = data.reservationStartDate;
      if (data.endDate) updatePayload.endDate = data.endDate;
      if (data.dropOffDate) updatePayload.dropOffDate = data.dropOffDate;
    }

    // Hotel/car: no totalPrice consistency constraint — send whenever present.
    if (props.type === 'hotel') {
      if (isFinite(basePriceVal)) updatePayload.basePrice = basePriceVal;
      const taxesAndFeesVal = parseFloat(data.taxesAndFees);
      if (isFinite(taxesAndFeesVal)) updatePayload.taxesAndFees = taxesAndFeesVal;
      const customerPriceVal = parseFloat(data.customerPrice);
      if (isFinite(customerPriceVal)) updatePayload.customerPrice = customerPriceVal;
      const resortFeeVal = parseFloat(data.resortFee);
      if (isFinite(resortFeeVal)) updatePayload.resortFee = resortFeeVal;
      if (data.departureDate) updatePayload.departureDate = data.departureDate;
    }

    if (props.type === 'car') {
      if (isFinite(basePriceVal)) updatePayload.basePrice = basePriceVal;
      if (isFinite(totalPriceVal)) updatePayload.totalPrice = totalPriceVal;
      const requiredChargesVal = parseFloat(data.requiredCharges);
      if (isFinite(requiredChargesVal)) updatePayload.requiredCharges = requiredChargesVal;
      const pricePerDayVal = parseFloat(data.pricePerDay);
      if (isFinite(pricePerDayVal)) updatePayload.pricePerDay = pricePerDayVal;
      // Agent-editable, never derived from dates — send whatever is in the
      // field (stored default or a manual override).
      const daysVal = parseInt(data.days, 10);
      updatePayload.days = isFinite(daysVal) && daysVal >= 1 ? daysVal : 1;
      if (data.carClass !== undefined) updatePayload.carClass = data.carClass;
      // Provider-local date + time (backend converts to UTC + provider-local).
      if (data.pickupDate) updatePayload.pickupDate = data.pickupDate;
      if (data.pickupTime) updatePayload.pickupTime = data.pickupTime;
      if (data.returnDate) updatePayload.returnDate = data.returnDate;
      if (data.returnTime) updatePayload.returnTime = data.returnTime;
      updatePayload.pickupLocation = {
        street: data.pickupStreet,
        city: data.pickupCity,
        state: data.pickupState,
        countryCode: data.pickupCountryCode,
        postalCode: data.pickupPostalCode,
      };
      updatePayload.returnLocation = {
        street: data.returnStreet,
        city: data.returnCity,
        state: data.returnState,
        countryCode: data.returnCountryCode,
        postalCode: data.returnPostalCode,
      };
      if (data.pickupCityCode !== undefined) updatePayload.pickupCityCode = data.pickupCityCode;
      if (data.returnCityCode !== undefined) updatePayload.returnCityCode = data.returnCityCode;
      // endDate is derived server-side from returnDate/returnTime (tz-aware) —
      // sending a client-computed endDate here used the OLD time suffix and
      // produced a wrong audit log value even though the DB write was correct.
    }


    // Close only after the API confirms success — not optimistically.
    props.updateBooking({ ...updatePayload, onSuccess: props.close });
  };

  return (
    <Dialog
      maxWidth={'md'}
      fullWidth={true}
      className={cx(styles.dialogBox, styles.modifyBookingDetailsPopup)}
      open={props.open}
      onClose={close}
    >
      <div className={styles.contentContainer}>
        <h1 className={styles.headerText}>Modify Booking Details</h1>
        <h2 className={styles.subText}>
          Modify the booking details stored in the TravelBank system
        </h2>
        <form
          className={styles.fieldContainer}
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextField
            label="User Email"
            defaultValue={props.userEmail}
            variant="outlined"
            error={!!get(errors, 'email')?.message}
            helperText={get(errors, 'email')?.message}
            inputProps={{
              ...register('email', {
                required: 'This field is required',
                pattern: {
                  message: 'Please enter a valid email address',
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/,
                },
              }),
            }}
          />
          <TextField
            label="Confirmation Number"
            defaultValue={props.confirmationNumber}
            variant="outlined"
            error={!!get(errors, 'confirmationNumber')?.message}
            helperText={get(errors, 'confirmationNumber')?.message}
            inputProps={{
              ...register('confirmationNumber'),
            }}
          />

          <Controller
            name={'status'}
            control={control}
            defaultValue={props.status}
            render={({ field }) => (
              <Select
                {...field}
                input={
                  <OutlinedInput
                    name={field.name}
                  />
                }
                variant="outlined"
              >
                <MenuItem
                  key="canceled"
                  value="canceled"
                >
                  Canceled
                </MenuItem>
                <MenuItem
                  key="pending"
                  value="pending"
                >
                  Pending
                </MenuItem>
                <MenuItem
                  key="errored"
                  value="errored"
                >
                  Errored
                </MenuItem>
                <MenuItem
                  key="completed"
                  value="completed"
                >
                  Completed
                </MenuItem>
              </Select>
            )}
          />
          <p className={styles.statusHelp}>
            Booking status stored in the TravelBank system. Updating it changes
            the saved record only — it does not contact the provider.
          </p>
          {props.booking?.type === 'car' && (
            <div className={styles.carSection}>
              <h3 className={styles.sectionHeader}>Pick-up</h3>
              <div className={styles.twoCol}>
                <div className={styles.inlineFieldWrapper}>
                  <span className={styles.inlineFieldLabel}>Pick-up Date</span>
                  <TextField
                    type="date"
                    size="small"
                    variant="outlined"
                    error={!!get(errors, 'pickupDate')?.message}
                    helperText={get(errors, 'pickupDate')?.message}
                    inputProps={{ ...register('pickupDate') }}
                  />
                </div>
                <Controller
                  name="pickupTime"
                  control={control}
                  defaultValue={carDefaults.pickupTime || '00:00'}
                  render={({ field }) => (
                    <TimeSelect
                      label="Pick-up Time"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <Accordion className={styles.locationAccordion} disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <span className={styles.accTitle}>Pick-up location</span>
                  <span className={styles.accSummary}>
                    {[watch('pickupCity'), watch('pickupState'), watch('pickupCountryCode')]
                      .filter(Boolean)
                      .join(', ') || 'Add address'}
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={styles.locGrid}>
                    <TextField className={styles.locGridFull} label="Street" size="small" variant="outlined" inputProps={{ ...register('pickupStreet') }} />
                    <TextField label="City" size="small" variant="outlined" inputProps={{ ...register('pickupCity') }} />
                    <TextField label="State" size="small" variant="outlined" inputProps={{ ...register('pickupState') }} />
                    <TextField label="Country Code" size="small" variant="outlined" inputProps={{ maxLength: 2, ...register('pickupCountryCode') }} />
                    <TextField label="Postal Code" size="small" variant="outlined" inputProps={{ ...register('pickupPostalCode') }} />
                    <TextField label="Airport/City Code" size="small" variant="outlined" inputProps={{ ...register('pickupCityCode') }} />
                  </div>
                </AccordionDetails>
              </Accordion>

              <h3 className={styles.sectionHeader}>Drop-off</h3>
              <div className={styles.twoCol}>
                <div className={styles.inlineFieldWrapper}>
                  <span className={styles.inlineFieldLabel}>Drop-off Date</span>
                  <TextField
                    type="date"
                    size="small"
                    variant="outlined"
                    error={!!get(errors, 'returnDate')?.message}
                    helperText={get(errors, 'returnDate')?.message}
                    inputProps={{ ...register('returnDate') }}
                  />
                </div>
                <Controller
                  name="returnTime"
                  control={control}
                  defaultValue={carDefaults.returnTime || '00:00'}
                  render={({ field }) => (
                    <TimeSelect
                      label="Drop-off Time"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <Accordion className={styles.locationAccordion} disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <span className={styles.accTitle}>Drop-off location</span>
                  <span className={styles.accSummary}>
                    {[watch('returnCity'), watch('returnState'), watch('returnCountryCode')]
                      .filter(Boolean)
                      .join(', ') || 'Add address'}
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  <div className={styles.locGrid}>
                    <TextField className={styles.locGridFull} label="Street" size="small" variant="outlined" inputProps={{ ...register('returnStreet') }} />
                    <TextField label="City" size="small" variant="outlined" inputProps={{ ...register('returnCity') }} />
                    <TextField label="State" size="small" variant="outlined" inputProps={{ ...register('returnState') }} />
                    <TextField label="Country Code" size="small" variant="outlined" inputProps={{ maxLength: 2, ...register('returnCountryCode') }} />
                    <TextField label="Postal Code" size="small" variant="outlined" inputProps={{ ...register('returnPostalCode') }} />
                    <TextField label="Airport/City Code" size="small" variant="outlined" inputProps={{ ...register('returnCityCode') }} />
                  </div>
                </AccordionDetails>
              </Accordion>

              <TextField
                label="Rental Car Class"
                size="small"
                variant="outlined"
                fullWidth
                inputProps={{ ...register('carClass') }}
              />

              <h3 className={styles.sectionHeader}>Pricing</h3>
              <div className={styles.twoCol}>
                <TextField
                  label="Days"
                  type="number"
                  size="small"
                  variant="outlined"
                  error={!!get(errors, 'days')?.message}
                  helperText={get(errors, 'days')?.message}
                  inputProps={{
                    step: '1',
                    min: 1,
                    ...register('days', { valueAsNumber: true, onChange: setCarSource('days'), min: { value: 1, message: 'Must be at least 1' } }),
                  }}
                />
                <TextField
                  label="Price per Day"
                  type="number"
                  size="small"
                  variant="outlined"
                  error={!!get(errors, 'pricePerDay')?.message}
                  helperText={get(errors, 'pricePerDay')?.message}
                  inputProps={{
                    step: '0.01',
                    min: 0,
                    ...register('pricePerDay', { valueAsNumber: true, onChange: setCarSource('pricePerDay'), min: { value: 0, message: 'Must be 0 or greater' } }),
                  }}
                />
              </div>
              <div className={styles.twoCol}>
                <TextField
                  label={`Base Price (${watchedDays} Days * Price per Day)`}
                  type="number"
                  size="small"
                  variant="outlined"
                  error={!!get(errors, 'basePrice')?.message}
                  helperText={get(errors, 'basePrice')?.message}
                  inputProps={{
                    step: '0.01',
                    min: 0,
                    ...register('basePrice', { valueAsNumber: true, onChange: setCarSource('basePrice'), min: { value: 0, message: 'Must be 0 or greater' } }),
                  }}
                />
                <TextField
                  label="Required Charges (Taxes & Fees)"
                  type="number"
                  size="small"
                  variant="outlined"
                  error={!!get(errors, 'requiredCharges')?.message}
                  helperText={get(errors, 'requiredCharges')?.message}
                  inputProps={{
                    step: '0.01',
                    min: 0,
                    ...register('requiredCharges', { valueAsNumber: true, onChange: setCarSource('requiredCharges'), min: { value: 0, message: 'Must be 0 or greater' } }),
                  }}
                />
              </div>
              <TextField
                label="Estimated Total"
                type="number"
                size="small"
                variant="outlined"
                fullWidth
                error={!!get(errors, 'totalPrice')?.message}
                helperText={get(errors, 'totalPrice')?.message}
                inputProps={{
                  step: '0.01',
                  min: 0,
                  ...register('totalPrice', { valueAsNumber: true, onChange: setCarSource('total'), min: { value: 0, message: 'Must be 0 or greater' } }),
                }}
              />
            </div>
          )}

          {props.booking?.type === 'hotel' && (
            <>
              <TextField
                label="Room"
                defaultValue={props.basePrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('basePrice', { valueAsNumber: true }),
                  step: '0.01',
                }}
              />
              <TextField
                label="Taxes and Fees"
                defaultValue={props.taxesAndFees}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('taxesAndFees', { valueAsNumber: true }),
                  step: '0.01',
                }}
              />
              <TextField
                label="Total Paid"
                defaultValue={props.customerPrice}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('customerPrice', { valueAsNumber: true }),
                  step: '0.01',
                }}
              />
              <TextField
                label="Resort Fee"
                defaultValue={props.resortFee}
                variant="outlined"
                type="number"
                inputProps={{
                  ...register('resortFee', { valueAsNumber: true }),
                  step: '0.01',
                }}
              />
              <TextField
                label="Departure Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={props.departureDate?.substring(0, 10)}
                variant="outlined"
                error={!!get(errors, 'departureDate')?.message}
                helperText={get(errors, 'departureDate')?.message}
                inputProps={{
                  ...register('departureDate', {
                    pattern: {
                      value: /^\d{4}-\d{2}-\d{2}$/,
                      message: 'Must be YYYY-MM-DD',
                    },
                  }),
                }}
              />
            </>
          )}

          {props.booking?.type === 'flight' &&
            segments.map((segment: any, index: number) => {
              return (
                <div
                  className={styles.segmentContainer}
                  key={`segment-fields-${index}`}
                >
                  <h2 className={styles.segmentHeader}>Segment {index + 1}</h2>
                  <div className={styles.segmentFieldContainer}>
                    <Controller
                      name={'segments.' + index + '.airline'}
                      control={control}
                      defaultValue={
                        AIRLINE_NAME_MAP[
                          (segment?.airline as keyof typeof AIRLINE_NAME_MAP) ||
                            (segment?.operatingAirline as keyof typeof AIRLINE_NAME_MAP)
                        ]
                      }
                      render={({ field }) => (
                        <AutoSuggest
                          {...field}
                          label={'Airline'}
                          className={styles.segmentAirlineContainer}
                          dataSet={airlines}
                          maxMenuHeight={90}
                        />
                      )}
                    />
                    <TextField
                      label="Flight Number"
                      defaultValue={segment.flightNumber}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.flightNumber')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.flightNumber')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.flightNumber', {
                          required: 'This is required',
                        }),
                      }}
                    />
                    <TextField
                      label="Air Plane Name"
                      defaultValue={segment.planeName}
                      variant="outlined"
                      inputProps={{
                        ...register('segments.' + index + '.planeName'),
                      }}
                    />
                  </div>
                  <div className={styles.segmentFieldContainer}>
                    <TextField
                      label="From Airport Code"
                      defaultValue={segment.from}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.from')?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.from')?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.from', {
                          required: 'This field is required',
                          pattern: {
                            message: 'Invalid 3 digit IATA code',
                            value: /^[a-zA-Z0-9]{3}$/,
                          },
                        }),
                      }}
                    />

                    <SvgAirplane
                      width={24}
                      height={20}
                    />
                    <TextField
                      label="To Airport Code"
                      defaultValue={segment.to}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.to')?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.to')?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.to', {
                          required: 'This field is required',
                          pattern: {
                            message: 'Invalid 3 digit IATA code',
                            value: /^[a-zA-Z0-9]{3}$/,
                          },
                        }),
                      }}
                    />
                  </div>
                  <div className={styles.segmentFieldContainer}>
                    <TextField
                      label="Depart Time (hh:mm 24hr)"
                      defaultValue={segment.departure?.substring(11, 16)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.departureTime')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.departureTime')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.departureTime', {
                          required: 'This field is required',
                          pattern: {
                            value: /^(?:[01]\d|2[0-3]):[0-5]\d$/,
                            message: 'Invalid hh:mm time value',
                          },
                        }),
                      }}
                    />
                    <TextField
                      label="Arrival Time (hh:mm 24hr)"
                      defaultValue={segment.arrival?.substring(11, 16)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.arrivalTime')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.arrivalTime')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.arrivalTime', {
                          required: 'This field is required',
                          pattern: {
                            value: /^(?:[01]\d|2[0-3]):[0-5]\d$/,
                            message: 'Invalid hh:mm time value',
                          },
                        }),
                      }}
                    />
                  </div>
                  <div className={styles.segmentFieldContainer}>
                    <TextField
                      label="Depart Date (YYYY-MM-DD)"
                      placeholder="YYYY-MM-DD"
                      defaultValue={segment.departure?.substring(0, 10)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.departureDate')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.departureDate')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.departureDate', {
                          required: 'This field is required',
                          pattern: {
                            value:
                              /^(?:\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2]\d|3[0-1]))$/,
                            message: 'Invalid YYYY-MM-DD value',
                          },
                        }),
                      }}
                    />
                    <TextField
                      label="Arrival Date (YYYY-MM-DD)"
                      placeholder="YYYY-MM-DD"
                      defaultValue={segment.arrival?.substring(0, 10)}
                      variant="outlined"
                      error={
                        !!get(errors, 'segments.' + index + '.arrivalDate')
                          ?.message
                      }
                      helperText={
                        get(errors, 'segments.' + index + '.arrivalDate')
                          ?.message
                      }
                      inputProps={{
                        ...register('segments.' + index + '.arrivalDate', {
                          required: 'This field is required',
                          pattern: {
                            value:
                              /^(?:\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2]\d|3[0-1]))$/,
                            message: 'Invalid YYYY-MM-DD value',
                          },
                        }),
                      }}
                    />
                  </div>
                  <input
                    {...register('segments.' + index + '.departure', {
                      value: segment.departure,
                    })}
                    type="hidden"
                  />
                  <input
                    {...register('segments.' + index + '.arrival', {
                      value: segment.arrival,
                    })}
                    type="hidden"
                  />
                </div>
              );
            })}

          {props.type === 'flight' && (
            <>
              <h2 className={styles.subText}>Pricing &amp; Dates</h2>

              <TextField
                label="Reservation Start Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={(props.booking?.arrivalDate ?? '').substring(0, 10)}
                variant="outlined"
                {...register('reservationStartDate')}
              />

              <TextField
                label="End Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={(props.booking?.endDate ?? '').substring(0, 10)}
                variant="outlined"
                {...register('endDate')}
              />

              <TextField
                label="Drop-off Date (YYYY-MM-DD)"
                placeholder="YYYY-MM-DD"
                defaultValue={(props.booking?.dropOffDate ?? '').substring(0, 10)}
                variant="outlined"
                {...register('dropOffDate')}
              />

              <TextField
                label="Base Price"
                type="number"
                defaultValue={defaultBasePrice}
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                {...register('basePrice')}
              />

              <TextField
                label="Tax Price"
                type="number"
                defaultValue={defaultTaxPrice}
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                {...register('taxPrice')}
              />

              <TextField
                label="Total Price (must equal Base + Tax)"
                type="number"
                defaultValue={defaultTotalPrice}
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                {...register('totalPrice')}
              />
            </>
          )}

          <div className={styles.actionButtonContainer}>
            <Button
              variant="outlined"
              onClick={() => onClose()}
            >
              Cancel Updates
            </Button>
            <Button
              className={styles.button}
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
